import {
  ApiDataSourceResponse,
  CoupledDimensionGroupResult,
  DataSourceCandidatesResponse,
  DimensionSource,
  isLegacyDataSourceResponse,
  isWidgetDimensionsResponse,
  isWidgetEntitiesResponse,
  SelectionSchemaMeasurePoint,
  Source,
  WidgetDimensionsResponse,
  WidgetEntitiesResponse,
  WidgetEntity,
} from '@highimpact-frontend/api-types';
import {
  collectFlatSources,
  dimensionSourceToSource,
  getDimensionSourceKey,
  getWidgetEntityKey,
} from '@highimpact/source-selector';

// -- type definitions --

export type AxisSourceSelection = Readonly<Record<string, readonly Source[]>>;

// -- helper functions --

/**
 * Builds a stable lookup key for a measure-point / measure-type pair.
 *
 * @param source - Source or partial source path.
 * @returns A string key in the form `measure_point_id:measure_type_id`.
 */
export function getSourceSelectionKey(source: {
  measure_point_id: number;
  measure_type_id: number;
}): string {
  return `${source.measure_point_id}:${source.measure_type_id}`;
}

/**
 * Resolves selected source paths against the fetched catalog, preserving order.
 *
 * Catalog entries take precedence over fallback sources so timezone and other
 * metadata from the API are available to preference-building consumers.
 *
 * @param fetchedSources - Fetched data-source catalog response, if available.
 * @param selectedSourcePaths - Selected measure-point paths in display order.
 * @param fallbackSources - Reconstituted or UI selection sources used when the catalog is unavailable.
 * @returns Resolved sources aligned with `selectedSourcePaths`.
 */
export function getSelectedSourcesFromFetchedCatalog(
  fetchedSources: ApiDataSourceResponse | null,
  selectedSourcePaths: readonly SelectionSchemaMeasurePoint[],
  fallbackSources: readonly Source[]
): Source[] {
  const selectedKeys = new Set(selectedSourcePaths.map(getSourceSelectionKey));

  if (selectedKeys.size === 0) {
    return [];
  }

  const fallbackByKey = new Map(
    fallbackSources.map((source) => [getSourceSelectionKey(source), source])
  );

  if (!fetchedSources) {
    return selectedSourcePaths
      .map((sourcePath) => fallbackByKey.get(getSourceSelectionKey(sourcePath)))
      .filter((source): source is Source => Boolean(source));
  }

  const flattenedRecords = collectFlatSources(fetchedSources.structure).records;
  const fetchedByKey = new Map<string, Source>();

  flattenedRecords.forEach(({ source }) => {
    const key = getSourceSelectionKey(source);
    if (selectedKeys.has(key) && !fetchedByKey.has(key)) {
      fetchedByKey.set(key, source);
    }
  });

  return selectedSourcePaths
    .map(
      (sourcePath) =>
        fetchedByKey.get(getSourceSelectionKey(sourcePath)) ??
        fallbackByKey.get(getSourceSelectionKey(sourcePath))
    )
    .filter((source): source is Source => Boolean(source));
}

/**
 * Returns `true` when two source lists describe the same ordered selection,
 * including timezone metadata when present on both sides.
 *
 * @param left - First ordered source list.
 * @param right - Second ordered source list.
 * @returns Whether both lists are equivalent for UI synchronization purposes.
 */
export function areSourceListsEqual(
  left: readonly Source[],
  right: readonly Source[]
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((leftSource, index) => {
    const rightSource = right[index];
    return (
      leftSource.measure_point_id === rightSource.measure_point_id &&
      leftSource.measure_type_id === rightSource.measure_type_id &&
      leftSource.timezone === rightSource.timezone
    );
  });
}

/**
 * Returns `true` when two axis-keyed source selections are equivalent.
 *
 * @param left - First axis selection map.
 * @param right - Second axis selection map.
 * @returns Whether every axis carries the same ordered sources.
 */
export function areAxisSourceSelectionsEqual(
  left: AxisSourceSelection,
  right: AxisSourceSelection
): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((axisKey) =>
    areSourceListsEqual(left[axisKey] ?? [], right[axisKey] ?? [])
  );
}

/**
 * Enriches every axis selection with catalog-backed source metadata.
 *
 * @param fetchedSources - Fetched data-source catalog response.
 * @param selection - Current axis-keyed source selection from the form.
 * @returns A new selection map with catalog-resolved sources per axis.
 */
export function syncAxisSourceSelectionFromCatalog<
  T extends AxisSourceSelection,
>(
  fetchedSources: ApiDataSourceResponse,
  selection: T
): { [K in keyof T]: Source[] } {
  return Object.fromEntries(
    Object.entries(selection).map(([axisKey, axisSources]) => [
      axisKey,
      getSelectedSourcesFromFetchedCatalog(
        fetchedSources,
        axisSources.map((source) => ({
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        })),
        axisSources
      ),
    ])
  ) as { [K in keyof T]: Source[] };
}

/**
 * Flattens an axis-keyed selection into a single ordered source list.
 *
 * @param selection - Axis-keyed source selection.
 * @param axisOrder - Axis keys in the order they should be concatenated.
 * @returns Sources from all axes in the requested order.
 */
export function flattenAxisSourceSelection(
  selection: AxisSourceSelection,
  axisOrder: readonly string[]
): Source[] {
  return axisOrder.flatMap((axisKey) => [...(selection[axisKey] ?? [])]);
}

/**
 * Indexes v2 dimension sources by measure-point and measure-type pair.
 */
function buildDimensionSourceLookup(
  response: WidgetDimensionsResponse
): Map<string, Source> {
  const lookup = new Map<string, Source>();

  const registerDimensionSource = (dimensionSource: DimensionSource) => {
    const key = getDimensionSourceKey(dimensionSource);
    if (!lookup.has(key)) {
      lookup.set(key, dimensionSourceToSource(dimensionSource));
    }
  };

  Object.values(response.independent_dimensions ?? {}).forEach(
    (dimensionSources) => {
      dimensionSources.forEach(registerDimensionSource);
    }
  );

  Object.values(response.coupled_groups ?? {}).forEach((coupledRows) => {
    coupledRows.forEach((row: CoupledDimensionGroupResult) => {
      Object.values(row).forEach(registerDimensionSource);
    });
  });

  return lookup;
}

/**
 * Resolves selected measure-point paths against a v2 dimensions catalog.
 *
 * @param fetchedSources - Flat dimensions response from the config API.
 * @param selectedSourcePaths - Selected measure-point paths in display order.
 * @param fallbackSources - Sources used when the catalog is unavailable.
 * @returns Resolved sources aligned with `selectedSourcePaths`.
 */
export function getSelectedSourcesFromV2DimensionsCatalog(
  fetchedSources: WidgetDimensionsResponse | null,
  selectedSourcePaths: readonly SelectionSchemaMeasurePoint[],
  fallbackSources: readonly Source[]
): Source[] {
  const selectedKeys = new Set(selectedSourcePaths.map(getSourceSelectionKey));

  if (selectedKeys.size === 0) {
    return [];
  }

  const fallbackByKey = new Map(
    fallbackSources.map((source) => [getSourceSelectionKey(source), source])
  );

  if (!fetchedSources) {
    return selectedSourcePaths
      .map((sourcePath) => fallbackByKey.get(getSourceSelectionKey(sourcePath)))
      .filter((source): source is Source => Boolean(source));
  }

  const fetchedByKey = buildDimensionSourceLookup(fetchedSources);

  return selectedSourcePaths
    .map(
      (sourcePath) =>
        fetchedByKey.get(getSourceSelectionKey(sourcePath)) ??
        fallbackByKey.get(getSourceSelectionKey(sourcePath))
    )
    .filter((source): source is Source => Boolean(source));
}

/**
 * Enriches every axis selection with v2 catalog-backed source metadata.
 *
 * @param fetchedSources - Flat dimensions response from the config API.
 * @param selection - Current axis-keyed source selection from the form.
 * @returns A new selection map with catalog-resolved sources per axis.
 */
export function syncAxisSourceSelectionFromV2DimensionsCatalog<
  T extends AxisSourceSelection,
>(
  fetchedSources: WidgetDimensionsResponse,
  selection: T
): { [K in keyof T]: Source[] } {
  return Object.fromEntries(
    Object.entries(selection).map(([axisKey, axisSources]) => [
      axisKey,
      getSelectedSourcesFromV2DimensionsCatalog(
        fetchedSources,
        axisSources.map((source) => ({
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        })),
        axisSources
      ),
    ])
  ) as { [K in keyof T]: Source[] };
}

/**
 * Resolves selected project-site pairs against a v2 entities catalog.
 *
 * @param fetchedEntities - Entity list response from the config API.
 * @param selectedEntities - Selected project-site pairs in display order.
 * @param fallbackEntities - Entities used when the catalog is unavailable.
 * @returns Resolved entities aligned with `selectedEntities`.
 */
export function getSelectedEntitiesFromFetchedCatalog(
  fetchedEntities: WidgetEntitiesResponse | null,
  selectedEntities: readonly Pick<WidgetEntity, 'project_id' | 'site_id'>[],
  fallbackEntities: readonly WidgetEntity[]
): WidgetEntity[] {
  if (selectedEntities.length === 0) {
    return [];
  }

  const fallbackByKey = new Map(
    fallbackEntities.map((entity) => [getWidgetEntityKey(entity), entity])
  );

  if (!fetchedEntities) {
    return selectedEntities
      .map((entity) => fallbackByKey.get(getWidgetEntityKey(entity)))
      .filter((entity): entity is WidgetEntity => Boolean(entity));
  }

  const fetchedByKey = new Map(
    fetchedEntities.entities.map((entity) => [
      getWidgetEntityKey(entity),
      entity,
    ])
  );

  return selectedEntities
    .map(
      (entity) =>
        fetchedByKey.get(getWidgetEntityKey(entity)) ??
        fallbackByKey.get(getWidgetEntityKey(entity))
    )
    .filter((entity): entity is WidgetEntity => Boolean(entity));
}

/**
 * Routes catalog resolution to the legacy tree or v2 dimensions path.
 *
 * @param fetchedSources - Any supported data-sources-candidates response.
 * @param selectedSourcePaths - Selected measure-point paths in display order.
 * @param fallbackSources - Sources used when the catalog is unavailable.
 * @returns Resolved sources aligned with `selectedSourcePaths`.
 */
export function resolveSourcesFromCatalog(
  fetchedSources: DataSourceCandidatesResponse | null,
  selectedSourcePaths: readonly SelectionSchemaMeasurePoint[],
  fallbackSources: readonly Source[]
): Source[] {
  if (!fetchedSources) {
    return getSelectedSourcesFromFetchedCatalog(
      null,
      selectedSourcePaths,
      fallbackSources
    );
  }

  if (isLegacyDataSourceResponse(fetchedSources)) {
    return getSelectedSourcesFromFetchedCatalog(
      fetchedSources,
      selectedSourcePaths,
      fallbackSources
    );
  }

  if (isWidgetDimensionsResponse(fetchedSources)) {
    return getSelectedSourcesFromV2DimensionsCatalog(
      fetchedSources,
      selectedSourcePaths,
      fallbackSources
    );
  }

  if (isWidgetEntitiesResponse(fetchedSources)) {
    return fallbackSources.filter((source) =>
      selectedSourcePaths.some(
        (sourcePath) =>
          getSourceSelectionKey(sourcePath) === getSourceSelectionKey(source)
      )
    );
  }

  return fallbackSources.filter((source) =>
    selectedSourcePaths.some(
      (sourcePath) =>
        getSourceSelectionKey(sourcePath) === getSourceSelectionKey(source)
    )
  );
}

/**
 * Enriches axis selections from either a legacy tree or v2 dimensions catalog.
 *
 * @param fetchedSources - Any supported data-sources-candidates response.
 * @param selection - Current axis-keyed source selection from the form.
 * @returns A new selection map with catalog-resolved sources per axis.
 */
export function syncAxisSourceSelectionFromCatalogResponse<
  T extends AxisSourceSelection,
>(
  fetchedSources: DataSourceCandidatesResponse,
  selection: T
): { [K in keyof T]: Source[] } {
  if (isLegacyDataSourceResponse(fetchedSources)) {
    return syncAxisSourceSelectionFromCatalog(fetchedSources, selection);
  }

  if (isWidgetDimensionsResponse(fetchedSources)) {
    return syncAxisSourceSelectionFromV2DimensionsCatalog(
      fetchedSources,
      selection
    );
  }

  return Object.fromEntries(
    Object.entries(selection).map(([axisKey, axisSources]) => [
      axisKey,
      [...axisSources],
    ])
  ) as { [K in keyof T]: Source[] };
}
